import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Table, Dialog, Input, Textarea, Alert, useToast } from '@dreamtree-org/twreact-ui';
import { Plus, Percent, ExternalLink, Copy } from 'lucide-react';
import { PageHeader, QueryState, EmptyState, SectionCard, StatusBadge, StatCard } from '@/components';
import { errorText, formatINR, formatDate } from '@/lib/format';
import { useQuery, useMutation } from '@/lib/useQuery';
import { useCan } from '@rbac/useCan';
import Can from '@rbac/Can';
import StatusTabs from '../components/StatusTabs';
import { VENDOR_TABS, VENDOR_ACTIONS, actionsFor, pctLabel, parsePct } from '../lib/vendors';
import { copyText } from '../../lib/clipboard';
import MailNotConfiguredHint from '../components/MailNotConfiguredHint';

const EMPTY_VENDOR = { business_name: '', contact_name: '', email: '', phone: '', gstin: '', commission: '' };

/**
 * Vendors (B17, ADR 0006): outside sellers on this storefront. Approve or
 * reject applications (vendors apply at /vendor), add a vendor yourself, set
 * commission per vendor and the store default, and open a vendor to review
 * its products.
 */
export default function Vendors() {
  const can = useCan();
  const nav = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState('pending');
  const list = useQuery('mktVendors', status ? { status } : {}, { select: (r) => r.data });
  const earnings = useQuery('mktEarnings', undefined, { select: (r) => r.data || [], enabled: can('marketplace', 'view') });
  const addM = useMutation('mktAddVendor');
  const reviewM = useMutation('mktReviewVendor');
  const defaultM = useMutation('mktSetDefaultCommission');

  const [adding, setAdding] = useState(null); // form
  const [created, setCreated] = useState(null); // { vendor, temporary_password, set_password_url, mailed, mail_error? }
  const [acting, setActing] = useState(null); // { vendor, action, note, commission }
  const [defaultForm, setDefaultForm] = useState(null); // string
  const [error, setError] = useState('');

  const data = list.data || { vendors: [], counts: {}, default_commission_pct: null };
  const counts = data.counts || {};
  const tabs = VENDOR_TABS.map((t) => ({ ...t, count: t.key ? counts[t.key] || 0 : Object.values(counts).reduce((a, b) => a + b, 0) }));
  const storePct = data.default_commission_pct;

  const submitAdd = async () => {
    setError('');
    const c = parsePct(adding.commission);
    if (c.error) return setError(c.error);
    try {
      const { commission, ...rest } = adding;
      const r = await addM.run({ ...rest, commission_pct: c.value });
      setAdding(null);
      setCreated(r.data);
      list.reload();
    } catch (ex) {
      setError(errorText(ex));
    }
  };

  const copy = async (text) => {
    if (await copyText(text)) toast.success('Copied');
    else toast.error('Copy failed', 'Select the text and copy it.');
  };

  const submitAction = async () => {
    setError('');
    const a = VENDOR_ACTIONS[acting.action];
    const payload = { action: acting.action, id: acting.vendor.id, note: acting.note || undefined, version: acting.vendor.version };
    if (acting.action === 'approve' && String(acting.commission || '').trim() !== '') {
      const c = parsePct(acting.commission);
      if (c.error) return setError(c.error);
      payload.commission_pct = c.value;
    }
    try {
      await reviewM.run(payload);
      toast.success(a.done, acting.vendor.business_name);
      setActing(null);
      list.reload();
    } catch (ex) {
      setError(errorText(ex));
    }
  };

  const submitDefault = async () => {
    setError('');
    const c = parsePct(defaultForm);
    if (c.error) return setError(c.error);
    try {
      await defaultM.run({ commission_pct: c.value });
      toast.success('Default commission saved', pctLabel(c.value));
      setDefaultForm(null);
      list.reload();
    } catch (ex) {
      setError(errorText(ex));
    }
  };

  // Per row: the review actions open to the vendor's current status (the server re-checks).
  const rowActions = (row) => [
    { name: 'open', label: 'Products', icon: <ExternalLink size={14} />, inline: true, onClick: () => nav(`/vendors/${row.id}`) },
    ...(can('marketplace', 'approve')
      ? actionsFor(row.status).map((a) => ({
        name: a.key, label: a.label, inline: a.key === 'approve',
        onClick: () => { setError(''); setActing({ vendor: row, action: a.key, note: '', commission: row.commission_pct ?? '' }); },
      }))
      : []),
  ];

  const ea = (earnings.data || []).reduce((s, r) => ({ sales: s.sales + r.sales_paise, commission: s.commission + r.commission_paise, earning: s.earning + r.earning_paise }), { sales: 0, commission: 0, earning: 0 });
  const actingDef = acting ? VENDOR_ACTIONS[acting.action] : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vendors"
        subtitle="Outside sellers who list on your storefront. Vendors apply at /vendor; approve them here, review each product they list, and set the commission you keep."
        actions={
          <div className="flex flex-wrap gap-2">
            <Can module="marketplace" action="update">
              <Button variant="outline" leftIcon={<Percent size={16} />} onClick={() => { setError(''); setDefaultForm(storePct == null ? '' : String(storePct)); }}>
                Default commission: {pctLabel(storePct)}
              </Button>
            </Can>
            <Can module="marketplace" action="create">
              <Button leftIcon={<Plus size={16} />} onClick={() => { setError(''); setAdding({ ...EMPTY_VENDOR }); }}>Add vendor</Button>
            </Can>
          </div>
        }
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Vendor sales" value={formatINR(ea.sales)} />
        <StatCard label="Your commission" value={formatINR(ea.commission)} />
        <StatCard label="Owed to vendors" value={formatINR(ea.earning)} />
      </div>

      {error && !adding && !acting && defaultForm == null ? <Alert variant="error" title="Not saved" dismissible onDismiss={() => setError('')}>{error}</Alert> : null}

      <StatusTabs tabs={tabs} value={status} onChange={setStatus} />
      <QueryState query={list} skeleton="table">
        {(d) =>
          d && d.vendors.length ? (
            <Table
              data={d.vendors}
              columns={[
                {
                  key: 'business_name', label: 'Vendor',
                  render: (v) => (
                    <button type="button" className="text-left" onClick={() => nav(`/vendors/${v.id}`)}>
                      <span className="block font-medium hover:underline">{v.business_name}</span>
                      <span className="block text-xs opacity-70">{v.number} · {v.contact_name}</span>
                    </button>
                  ),
                },
                { key: 'email', label: 'Contact', render: (v) => <span className="text-sm">{v.email}<span className="block text-xs opacity-70">{v.phone}</span></span>, hideOnMobile: true },
                { key: 'status', label: 'Status', render: (v) => <StatusBadge status={v.status} /> },
                { key: 'commission_pct', label: 'Commission', render: (v) => pctLabel(v.commission_pct, storePct) },
                { key: 'source', label: 'Joined', render: (v) => <span className="text-sm">{v.source === 'added' ? 'Added by store' : 'Applied'}<span className="block text-xs opacity-70">{formatDate(v.created_at)}</span></span>, hideOnMobile: true },
              ]}
              actions={rowActions}
              replaceDefaultActions
              withAction
              showSerial={false}
              showColumnPicker={false}
              globalSearch
              pagination
              pageSize={25}
            />
          ) : (
            <EmptyState title={status === 'pending' ? 'No vendor requests' : 'No vendors here'}>
              {status === 'pending' ? 'Vendors apply from your store’s /vendor page. Their requests show up here for you to approve.' : 'Add a vendor yourself or approve a request.'}
            </EmptyState>
          )
        }
      </QueryState>

      <SectionCard title="Earnings by vendor">
        <QueryState query={earnings} skeleton="table">
          {(rows) => (rows && rows.length ? (
            <Table
              data={rows.map((r) => ({ ...r, id: r.vendor.id }))}
              columns={[
                { key: 'vendor', label: 'Vendor', render: (r) => <span className="font-medium">{r.vendor.name}</span> },
                { key: 'lines', label: 'Items sold', render: (r) => r.lines, hideOnMobile: true },
                { key: 'sales_paise', label: 'Sales', render: (r) => formatINR(r.sales_paise) },
                { key: 'commission_paise', label: 'Commission', render: (r) => formatINR(r.commission_paise) },
                { key: 'earning_paise', label: 'Vendor share', render: (r) => formatINR(r.earning_paise) },
                { key: 'delivered_earning_paise', label: 'Due (delivered)', render: (r) => formatINR(r.delivered_earning_paise), hideOnMobile: true },
              ]}
              showSerial={false}
              showColumnPicker={false}
              withAction={false}
            />
          ) : <p className="text-sm opacity-60">No vendor sales yet. Commission is recorded on each order line when it is placed; settle with vendors outside the app.</p>)}
        </QueryState>
      </SectionCard>

      <Dialog isOpen={!!adding} onClose={() => setAdding(null)} title="Add a vendor" size="md">
        {adding ? (
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); submitAdd(); }}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Business name" value={adding.business_name} onChange={(e) => setAdding({ ...adding, business_name: e.target.value })} required maxLength={160} />
              <Input label="Contact person" value={adding.contact_name} onChange={(e) => setAdding({ ...adding, contact_name: e.target.value })} required maxLength={120} />
              <Input label="Email" type="email" value={adding.email} onChange={(e) => setAdding({ ...adding, email: e.target.value })} required />
              <Input label="Phone" inputMode="tel" value={adding.phone} onChange={(e) => setAdding({ ...adding, phone: e.target.value })} required />
              <Input label="GSTIN" value={adding.gstin} onChange={(e) => setAdding({ ...adding, gstin: e.target.value.toUpperCase() })} helperText="Optional" maxLength={15} />
              <Input label="Commission (%)" inputMode="decimal" value={adding.commission} onChange={(e) => setAdding({ ...adding, commission: e.target.value })} helperText={`Empty uses the store default (${pctLabel(storePct)}).`} />
            </div>
            <p className="text-sm opacity-70">The vendor is approved straight away and gets an email with a temporary password and a link to choose their own. They sign in at /vendor.</p>
            {error ? <Alert variant="error" title="Not added">{error}</Alert> : null}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setAdding(null)}>Cancel</Button>
              <Button type="submit" loading={addM.loading}>Add vendor</Button>
            </div>
          </form>
        ) : null}
      </Dialog>

      <Dialog isOpen={!!created} onClose={() => setCreated(null)} title="Vendor added" size="sm">
        {created ? (
          <div className="space-y-3">
            <p className="text-sm"><span className="font-semibold">{created.vendor.business_name}</span> can now sign in at <code>{window.location.origin}/vendor</code>.</p>
            {created.mailed ? (
              <Alert variant="success" title={`Sign-in details sent to ${created.vendor.email}`}>The email has the temporary password and a link to choose their own password (valid for 7 days).</Alert>
            ) : (
              <Alert variant="warning" title="Email not sent">
                <p>Share the temporary password or this set-password link with the vendor yourself. The link works once, for 7 days.</p>
                <MailNotConfiguredHint reason={created.mail_error} />
              </Alert>
            )}
            <SectionCard title="Sign-in details">
              <div className="space-y-1 text-sm">
                <div>Email: <code>{created.vendor.email}</code></div>
                <div className="flex items-center gap-2">Temporary password: <code className="font-semibold">{created.temporary_password}</code>
                  <Button size="sm" variant="ghost" leftIcon={<Copy size={14} />} onClick={() => copy(created.temporary_password)}>Copy</Button>
                </div>
                {!created.mailed && created.set_password_url ? (
                  <div className="flex items-center gap-2 min-w-0">Set-password link: <code className="truncate">{created.set_password_url}</code>
                    <Button size="sm" variant="ghost" leftIcon={<Copy size={14} />} onClick={() => copy(created.set_password_url)}>Copy link</Button>
                  </div>
                ) : null}
              </div>
            </SectionCard>
            <Alert variant="info" title="Shown once">The temporary password is not shown again. If it is lost, the vendor can use Forgot password on the sign-in page.</Alert>
            <div className="flex justify-end"><Button onClick={() => setCreated(null)}>Done</Button></div>
          </div>
        ) : null}
      </Dialog>

      <Dialog isOpen={!!acting} onClose={() => setActing(null)} title={acting ? `${actingDef.label} ${acting.vendor.business_name}?` : ''} size="sm">
        {acting ? (
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); submitAction(); }}>
            {acting.action === 'approve' ? (
              <Input label="Commission (%)" inputMode="decimal" value={acting.commission} onChange={(e) => setActing({ ...acting, commission: e.target.value })} helperText={`Empty uses the store default (${pctLabel(storePct)}).`} />
            ) : null}
            {acting.action === 'suspend' ? <p className="text-sm opacity-80">Their products leave the storefront and they are signed out. Orders already placed still ship.</p> : null}
            <Textarea
              label={actingDef.note ? 'Reason (the vendor sees this)' : 'Note (optional)'}
              value={acting.note}
              onChange={(e) => setActing({ ...acting, note: e.target.value })}
              rows={3}
              maxLength={500}
              required={actingDef.note}
            />
            {error ? <Alert variant="error" title="Not saved">{error}</Alert> : null}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setActing(null)}>Cancel</Button>
              <Button type="submit" variant={actingDef.tone === 'danger' ? 'destructive' : 'primary'} loading={reviewM.loading} disabled={actingDef.note && !acting.note.trim()}>{actingDef.label}</Button>
            </div>
          </form>
        ) : null}
      </Dialog>

      <Dialog isOpen={defaultForm != null} onClose={() => setDefaultForm(null)} title="Default vendor commission" size="sm">
        {defaultForm != null ? (
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); submitDefault(); }}>
            <Input label="Commission (%)" inputMode="decimal" value={defaultForm} onChange={(e) => setDefaultForm(e.target.value)} helperText="Used when neither the product nor the vendor has its own rate. Applies to new orders only." />
            {error ? <Alert variant="error" title="Not saved">{error}</Alert> : null}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDefaultForm(null)}>Cancel</Button>
              <Button type="submit" loading={defaultM.loading}>Save</Button>
            </div>
          </form>
        ) : null}
      </Dialog>
    </div>
  );
}

